import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Switch } from "./ui/switch";
import { AtSign, ChevronDown, ChevronUp } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

// SuperAdmin: verifica dominio mittenti @giniusapp.it + panoramica alias configurati.
export default function SuperAdminEmailSender() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setData((await api.get("/superadmin/email-sender/overview")).data); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const toggleVerificato = async (v) => {
    if (v && !window.confirm(`Confermi che il dominio ${data?.dominio} è verificato su Mailjet (SPF/DKIM)? Da questo momento le email degli studi partiranno dai loro alias.`)) return;
    setBusy(true);
    try {
      const res = await api.post("/superadmin/email-sender/dominio", { verificato: v });
      setData((d) => ({ ...d, verificato: res.data.verificato }));
      toast.success(v ? "Dominio marcato come verificato: alias attivi" : "Verifica dominio disattivata: fallback al mittente di piattaforma");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore");
    } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <Card className="p-4 sm:p-5 border-2 mt-6" data-testid="superadmin-email-sender">
      <button type="button" className="w-full flex items-center justify-between gap-2 text-left" onClick={() => setOpen(!open)} data-testid="email-sender-overview-toggle">
        <div className="flex items-center gap-2 min-w-0">
          <AtSign className="w-5 h-5 text-brand shrink-0" />
          <h3 className="font-display text-lg font-bold truncate">Mittenti email @{data.dominio}</h3>
          <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${data.verificato ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
            {data.verificato ? "Dominio verificato" : "In attesa verifica"}
          </span>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-slate-50 rounded-lg p-4 mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Dominio verificato su Mailjet (SPF/DKIM)</p>
              <p className="text-xs text-slate-500 break-words">
                Finché è spento, tutte le email partono dal mittente di piattaforma ({data.mittente_fallback}).
                Attivalo dopo aver aggiunto e validato il dominio {data.dominio} nell'account Mailjet.
              </p>
            </div>
            <Switch data-testid="email-sender-domain-toggle" checked={data.verificato} disabled={busy} onCheckedChange={toggleVerificato} />
          </div>
          <p className="text-sm font-semibold mb-2">Alias configurati ({data.mittenti.length})</p>
          {data.mittenti.length === 0 ? (
            <p className="text-sm text-slate-400">Nessuno studio ha ancora configurato il proprio mittente.</p>
          ) : (
            <div className="space-y-2">
              {data.mittenti.map((m) => (
                <div key={m.tenant_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm border rounded-lg px-3 py-2" data-testid={`email-sender-row-${m.tenant_id}`}>
                  <span className="font-bold break-all">{m.alias}</span>
                  <span className="text-slate-500 truncate">"{m.display_name}"</span>
                  <span className="text-slate-400 ml-auto whitespace-nowrap">{m.tenant_nome} · {fmtDate(m.configured_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
